import * as logClient from "@classdojo/log-client";
import once from "lodash/once";
import { useEffect, useCallback, useRef } from "react";
import { useTranslation } from "@web-monorepo/i18next";
import FullBleedScreen from "components/BeyondToolkit/FullBleedScreen";
import ToolkitAudioActivityChrome from "components/ToolkitAudioActivityChrome";
import { useAudioPlayer, playStates } from "hooks/useAudioPlayer";
import { openPointsSheet } from "src/pods/webviewInit";
import { logParentToolkitEvent } from "src/utils/logEvent";
import { SHAKE_SILLIES } from "utils/beyondToolkitTools";
import { sendUsage } from "utils/freemium";

export default function ShakeSillies() {
  const shakeSillies = SHAKE_SILLIES();
  // this tells i18next to load these namespaces
  const { ready } = useTranslation(["dojo.beyond"]);

  useEffect(() => {
    logParentToolkitEvent({ eventName: `${shakeSillies.loggingId}.loaded` });
  }, [shakeSillies.loggingId]);

  const sendUsageRef = useRef(once(() => sendUsage("wiggle")));

  const {
    play: playMain,
    pause,
    playState,
    secondsRemaining,
  } = useAudioPlayer({
    src: shakeSillies.mainAudio,
    expectedDuration: shakeSillies.duration,
  });

  const { play: playIntro, pause: pauseIntro } = useAudioPlayer({
    src: "/static/media/activity-wiggleit_start.mp3",
    expectedDuration: 17,
  });

  const { play: playOutro } = useAudioPlayer({
    src: "/static/media/activity-wiggleit_end.mp3",
    expectedDuration: 3,
  });

  // play intro audio clip on mount

  useEffect(() => {
    playIntro();
  }, [playIntro]);

  // pause intro clip when we start the main clip
  const play = useCallback(() => {
    pauseIntro();
    playMain();

    // send usage on first play event, and not on subsequent play events (i.e. after pause)
    sendUsageRef.current();
  }, [pauseIntro, playMain]);

  // play outro after the main clip

  useEffect(() => {
    if (playState === playStates.DONE) {
      playOutro();
      logParentToolkitEvent({ eventName: `${shakeSillies.loggingId}.activity_completed` });
    }
  }, [shakeSillies.loggingId, playOutro, playState]);

  const videoNodeRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    if (videoNodeRef.current) {
      if (playState === playStates.PLAYING) {
        videoNodeRef.current.play().catch(() => {
          logClient.sendMetrics([
            {
              type: "increment",
              value: 1,
              metricName: "toolkit.media.video.play.error",
            },
          ]);
        });
      } else if (playState === playStates.PAUSED) {
        videoNodeRef.current.pause();
      }
    }
  }, [playState]);

  if (!ready) return null;

  return (
    <FullBleedScreen background={shakeSillies.color}>
      <ToolkitAudioActivityChrome
        loggingId={shakeSillies.loggingId}
        play={play}
        pause={pause}
        playState={playState}
        secondsRemaining={secondsRemaining}
        closeAndGivePoints={() => {
          openPointsSheet();
          window.history.back();
        }}
        mainContent={
          <video
            playsInline
            preload="auto"
            ref={(n) => (videoNodeRef.current = n)}
            src={"/static/media/wiggleit.mp4#t=0.1"}
            loop
            sx={{
              maxWidth: "75vw",
              width: 400,
            }}
          />
        }
      />
    </FullBleedScreen>
  );
}
